import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { NymblTranslatePipe } from '@shared/pipes';
import { NotificationStore } from '@core/services';
import { NotificationPanel } from './notification-panel';

/**
 * Topbar notification bell: unread badge plus the slide-out panel.
 *
 * "View All" inside the panel is the way to the full page, matching v1, where the bell
 * toggles the right sidebar (`app/views/header.html:104`) and the sidebar links onward.
 *
 * Lives here rather than in `features/notifications/` because it is topbar chrome, alongside
 * `main-company-selector` and `user-profile-menu`, and because nothing in `app-layout` imports
 * from `@features/*` — doing so would invert the layering.
 *
 * The store is loaded by the topbar, not here, so the badge is populated whether or not the
 * user ever opens the panel.
 */
@Component({
  selector: 'app-notification-bell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonModule, NotificationPanel, NymblTranslatePipe, Tooltip],
  template: `
    <p-button
      [text]="true"
      icon="pi pi-bell"
      [badge]="badge()"
      badgeSeverity="danger"
      [ariaLabel]="ariaLabel()"
      [pTooltip]="'notifications.bell.label' | nymblTranslate"
      (onClick)="togglePanel()"
    />

    <app-notification-panel
      [(visible)]="panelOpen"
      [userId]="userId()"
      [userName]="userName()"
    />
  `
})
export class NotificationBell {
  private store = inject(NotificationStore);
  private nymblTranslate = inject(NymblTranslatePipe);

  /** Current user id, forwarded to the panel for the two bulk endpoints. */
  readonly userId = input<number | null>(null);

  /**
   * Current user's display name, forwarded to the panel's compose dialog, which prefixes an
   * outgoing reply's body with it the way v1's `SendMessageCtrl` does.
   */
  readonly userName = input<string>('');

  protected readonly panelOpen = signal<boolean>(false);

  /**
   * `p-button` hides the badge when the value is undefined, which is what we want at zero —
   * the badge clears rather than showing a "0".
   */
  protected readonly badge = computed(() => {
    const count = this.store.unreadCount();
    return count > 0 ? String(count) : undefined;
  });

  /**
   * The accessible name carries the count, so a screen reader hears "3 unread notifications"
   * rather than just "Notifications" with a silent visual badge.
   */
  protected readonly ariaLabel = computed(() => {
    const base = this.nymblTranslate.transform('notifications.bell.label');
    const count = this.store.unreadCount();
    if (count === 0) return base;
    return `${base}: ${count} ${this.nymblTranslate.transform(
      'notifications.panel.unread_badge_label'
    )}`;
  });

  protected togglePanel(): void {
    this.panelOpen.update((open) => !open);
  }
}
